import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { GroupedSalesLocations } from "../../../types/SalesLocation";
import { useCallback, useMemo, memo } from "react";
import { useTranslation } from "@coworker/locales";
import { FixaSkeleton } from "@coworker/apprestructured/src/shared/wrappers/FixaSkeleton/FixaSkeleton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import ModalColumn from "../../ModalColumn/ModalColumn";
import { ItemStockAndEds } from "@coworker/apprestructured/src/shared/components/item-cards/ItemStockAndEds/ItemStockAndEds";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { SalesLocation } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import styles from "./LocationsTab.module.css";

type LocationsTabProps = {
  itemNoAndType: ItemNoAndType | null;
  selectedSlid: string;
  selectedCustomLocation: string;
  onSlidChange: (location: string) => void;
  onCustomLocationSelect: (location: string) => void;
  isLoadingLocations: boolean;
  filteredLocations: GroupedSalesLocations;
  totalAssignedSalesSpaceQuantity: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  withCustomLocations?: boolean;
};

const LocationsTab = memo(
  ({
    itemNoAndType,
    selectedSlid,
    selectedCustomLocation,
    onSlidChange,
    onCustomLocationSelect,
    searchQuery,
    setSearchQuery,
    isLoadingLocations,
    filteredLocations: {
      homeAndPrimaryLocations,
      sgfLocations,
      otherSalesLocations,
      customLocations,
    },
    totalAssignedSalesSpaceQuantity,
    withCustomLocations = false,
  }: LocationsTabProps) => {
    const { t } = useTranslation();

    const locationSection = useCallback(
      (locations: SalesLocation[], titleKey: string, isCustom = false) => {
        if (isLoadingLocations) return <FixaSkeleton height="100px" />;
        if (locations.length === 0) return null;

        return [
          <FixaListViewItem
            key={`${titleKey}-header`}
            title={t(titleKey)}
            quantityLabel={isCustom ? undefined : <span>SSQ</span>}
          />,
          ...locations.map((location) => (
            <FixaListViewItem
              id={`${isCustom ? "custom-location" : "location"}-${location.slid}`}
              key={`${titleKey}-${location.slid}`}
              title={location.departmentName}
              description={isCustom ? undefined : location.slid}
              emphasised
              quantityLabel={
                isCustom ? undefined : (
                  <span>{location.assignedSalesSpaceQuantity}</span>
                )
              }
              onChange={() =>
                isCustom
                  ? onCustomLocationSelect(location.slid)
                  : onSlidChange(location.slid)
              }
              control="radiobutton"
              controlProps={{
                checked: isCustom
                  ? !selectedSlid && selectedCustomLocation === location.slid
                  : location.slid === selectedSlid,
                value: location.slid,
              }}
            />
          )),
        ];
      },
      [
        selectedSlid,
        selectedCustomLocation,
        isLoadingLocations,
        t,
        onSlidChange,
        onCustomLocationSelect,
      ]
    );

    const mainLocationsContent = useMemo(
      () => locationSection(homeAndPrimaryLocations, "homeAndPrimaryString"),
      [homeAndPrimaryLocations, locationSection]
    );

    const otherLocationsContent = useMemo(
      () => locationSection(otherSalesLocations, "otherLocationsString"),
      [otherSalesLocations, locationSection]
    );

    const sgfLocationsContent = useMemo(
      () => locationSection(sgfLocations, "sgfLocationsString"),
      [sgfLocations, locationSection]
    );

    const customLocationsContent = useMemo(() => {
      if (!withCustomLocations) return null;
      const customSalesLocations = (customLocations ?? []).map((location) => ({
        slid: location,
        specialityShopName: location,
        assignedSalesSpaceQuantity: 0,
        departmentName: location,
      }));
      return locationSection(
        customSalesLocations,
        "customLocationsString",
        true
      );
    }, [withCustomLocations, customLocations, locationSection]);

    const hasAnyLocations =
      (homeAndPrimaryLocations ?? []).length > 0 ||
      (otherSalesLocations ?? []).length > 0 ||
      (sgfLocations ?? []).length > 0 ||
      (withCustomLocations && (customLocations ?? []).length > 0);

    const totalCapacityContent = useMemo(() => {
      if (isLoadingLocations) return <FixaSkeleton height="50px" />;

      if (!hasAnyLocations) return null;

      return (
        <FixaListView id={"total-capacity"}>
          <FixaListViewItem
            className={styles["totalCapacity"]}
            title={`${t("totalCapacityString")} (ASSQ)`}
            quantityLabel={
              <span>
                {totalAssignedSalesSpaceQuantity !== 0
                  ? totalAssignedSalesSpaceQuantity
                  : "-"}
              </span>
            }
            showDivider={false}
            inset
          />
        </FixaListView>
      );
    }, [
      isLoadingLocations,
      hasAnyLocations,
      totalAssignedSalesSpaceQuantity,
      t,
    ]);

    const hasSearchQuery = searchQuery.trim().length > 0;

    const noResultsContent = useMemo(() => {
      if (isLoadingLocations || hasAnyLocations) return null;

      return (
        <div className={styles["noResults"]}>
          <FixaText textStyle="Heading.L">{t("noTaskResultsString")}</FixaText>
          <FixaText textStyle="Body.L">
            {hasSearchQuery
              ? t("noResultsForString", { query: searchQuery })
              : t("noLocationsForArticleString")}
          </FixaText>
        </div>
      );
    }, [isLoadingLocations, hasAnyLocations, hasSearchQuery, searchQuery, t]);

    const disableSearch =
      isLoadingLocations ||
      itemNoAndType === null ||
      (!hasSearchQuery && !hasAnyLocations);

    const locationSearchContent = useMemo(() => {
      if (isLoadingLocations || (!hasAnyLocations && !hasSearchQuery))
        return null;
      return (
        <FixaSearch
          disabled={disableSearch ?? false}
          id="locations-search"
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
          placeholder={t("searchLocationString")}
          value={searchQuery}
        />
      );
    }, [
      isLoadingLocations,
      hasAnyLocations,
      hasSearchQuery,
      disableSearch,
      searchQuery,
      t,
      setSearchQuery,
    ]);

    if (itemNoAndType === null) {
      return (
        <ModalColumn gap="medium" className={styles["noLocations"]}>
          <FixaText textStyle="Heading.M">
            {t("noLocationsTitleString")}
          </FixaText>
          <FixaText data-testid="select-article-message" textStyle="Body.M">
            {t("selectArticleLocationString")}
          </FixaText>
        </ModalColumn>
      );
    }
    return (
      <ModalColumn gap="medium">
        {itemNoAndType && (
          <>
            <ItemStockAndEds
              itemNo={itemNoAndType?.no || ""}
              itemType={itemNoAndType?.type || ""}
            />
            {locationSearchContent}
            {totalCapacityContent}
            <FixaListView id="locations" className={styles["section"]}>
              {mainLocationsContent}
              {otherLocationsContent}
              {sgfLocationsContent}
              {customLocationsContent}
            </FixaListView>
            {noResultsContent}
          </>
        )}
      </ModalColumn>
    );
  }
);

export default LocationsTab;
